import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CircleAlert, CircleCheck, Clock3, Loader2, Search, SearchCheck, ShieldCheck, ShoppingCart } from 'lucide-react';
import { PURCHASE_LIMITS } from '@cp/shared';
import {
  Banner,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Fact,
  Facts,
  Field,
  Input,
  LiveStatus,
  ProgressBar,
  Select,
  Spinner,
  StatusPill,
  Steps,
} from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { PurchaseLog } from '@/components/domains/PurchaseLog';
import { useCancelPurchase, useConfirmPurchase, useDomainPurchase, useStartPurchase } from '@/hooks/useDomainPurchases';
import { useDomainCheck } from '@/hooks/useSeoMetrics';
import { toast } from '@/store/toastStore';
import { errorText, providerSaid } from '@/utils/errors';
import { PURCHASE_STATUS } from '@/utils/labels';
import {
  PURCHASE_STAGES,
  buyingState,
  checkingState,
  chosenItems,
  formatMoney,
  purchaseProgress,
  purchaseStage,
  type PurchaseStage,
} from '@/utils/purchases';
import { DOMAIN_VERDICT } from '@/utils/seo';
import { isDomainName } from '@/utils/validation';
import type { DomainPurchaseView, DomainPurchasesOverview } from '@/types/api';

const YEARS = [1, 2, 3, 5, 10].map((years) => ({ value: String(years), label: years === 1 ? '1 год' : years < 5 ? `${years} года` : `${years} лет` }));

/** The names typed in: any separator, any case, each once. */
function namesOf(text: string): string[] {
  const names = text
    .split(/[\s,;]+/)
    .map((name) => name.trim().toLowerCase().replace(/\.$/, ''))
    .filter(Boolean);
  return [...new Set(names)];
}

/** What 2d2s said, when it was 2d2s that refused: its words say why (no money, no rights). */
function purchaseError(error: unknown, fallback: string): string {
  const said = providerSaid(error);
  return said ? `2d2s ответил: ${said}` : errorText(error, fallback);
}

const plural = (count: number, one: string, few: string, many: string) => (count % 10 === 1 && count % 100 !== 11 ? one : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20) ? few : many);

/** The wizard's five steps across the top: done, current, still ahead. */
function WizardSteps({ stage }: { readonly stage: PurchaseStage }): ReactNode {
  const current = PURCHASE_STAGES.findIndex((item) => item.id === stage);
  return (
    <ol className="wizard-steps" aria-label="Шаги покупки">
      {PURCHASE_STAGES.map((item, index) => (
        <li key={item.id} className={index < current ? 'wizard-step wizard-step-done' : index === current ? 'wizard-step wizard-step-current' : 'wizard-step'} aria-current={index === current ? 'step' : undefined}>
          <span className="wizard-step-number">{index < current ? <CircleCheck size={14} aria-hidden="true" /> : index + 1}</span>
          <span className="wizard-step-label">{item.label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Buying domains (ADR-0039) as a wizard (ADR-0053): the names, 2d2s's check
 * of which are free, the choice and the one confirmation that spends money,
 * the purchase and DNS name by name, and the result with what to do next.
 * Every step after the first follows 2d2s live, every two seconds; the window
 * can be closed at any time and the purchase reopened from the list at the
 * step it has reached.
 */
export function PurchaseWizard({
  open,
  purchaseId,
  overview,
  onClose,
}: {
  readonly open: boolean;
  /** An existing purchase to follow; none to start a new one. */
  readonly purchaseId: string | null;
  readonly overview: DomainPurchasesOverview;
  readonly onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [started, setStarted] = useState<string | null>(null);
  const id = purchaseId ?? started;
  const purchase = useDomainPurchase(id);
  const view = purchase.data;
  const stage: PurchaseStage = id ? purchaseStage(view) : 'names';

  // The names step.
  const [text, setText] = useState('');
  const [buy, setBuy] = useState('1');
  const [years, setYears] = useState('1');
  const [autoRenew, setAutoRenew] = useState(true);
  const [privacy, setPrivacy] = useState(true);
  const [shown, setShown] = useState(false);
  const start = useStartPurchase({ onSuccess: (result) => setStarted(result.id) });

  // The choice.
  const [picked, setPicked] = useState<ReadonlySet<string> | null>(null);
  const [confirming, setConfirming] = useState(false);
  const confirm = useConfirmPurchase(id ?? '', {
    onSuccess: () => {
      setConfirming(false);
      toast.success('Покупка подтверждена', 'Ход покупки — в этом окне и в списке покупок');
    },
  });
  const cancel = useCancelPurchase(id ?? '', { onSuccess: () => toast.success('Покупка отменена') });

  const close = (): void => {
    setStarted(null);
    setPicked(null);
    setShown(false);
    onClose();
  };

  const names = namesOf(text);
  const count = Number(buy);
  const invalid = names.filter((name) => !isDomainName(name));
  const problems: Partial<Record<'names' | 'buy', string>> = {};
  if (names.length === 0) problems.names = 'Нужно хотя бы одно имя';
  else if (invalid.length > 0) problems.names = `Не похоже на доменное имя: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`;
  else if (names.length > PURCHASE_LIMITS.candidates) problems.names = `Не больше ${PURCHASE_LIMITS.candidates} имён за раз`;
  if (!Number.isInteger(count) || count < 1 || count > PURCHASE_LIMITS.buy) problems.buy = `От 1 до ${PURCHASE_LIMITS.buy}`;
  else if (names.length > 0 && count > names.length) problems.buy = 'Больше, чем имён в списке';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setShown(true);
    if (Object.keys(problems).length > 0 || start.isPending) return;
    start.mutate({ candidates: names, buy: count, years: Number(years), autoRenew, privacy });
  };

  const shortlist = view ? shortlistOf(view) : new Set<string>();
  const chosen = picked ?? shortlist;
  const bought = view ? view.items.filter((item) => item.purchased).map((item) => item.domain) : [];

  const titles: Record<PurchaseStage, string> = {
    names: 'Купить домены',
    checking: 'Проверка доступности',
    choose: 'Выбор доменов',
    buying: 'Покупка',
    done:
      view?.status === 'COMPLETED'
        ? 'Домены куплены'
        : view?.status === 'PARTIALLY_COMPLETED'
          ? 'Куплено частично'
          : view?.status === 'CANCELLED'
            ? 'Покупка отменена'
            : 'Покупка не состоялась',
  };
  const description =
    stage === 'names'
      ? 'Оплата — только после подтверждения'
      : stage === 'done'
        ? `${view?.requestedBy ?? ''} · ${view ? new Date(view.createdAt).toLocaleString('ru-RU') : ''}`
        : 'Окно можно закрыть: покупка продолжится, её видно в списке';

  const cancelButton =
    view?.canCancel && stage !== 'done' ? (
      <Button onClick={() => cancel.mutate()} busy={cancel.isPending} disabled={confirm.isPending}>
        Отменить покупку
      </Button>
    ) : null;

  const footer = (() => {
    switch (stage) {
      case 'names':
        return (
          <>
            <Button onClick={close} disabled={start.isPending}>
              Отмена
            </Button>
            <Button type="submit" form="purchase-start" variant="primary" icon={Search} busy={start.isPending}>
              Проверить доступность
            </Button>
          </>
        );
      case 'checking':
      case 'buying':
        return (
          <>
            {cancelButton}
            <Button onClick={close}>Закрыть</Button>
          </>
        );
      case 'choose':
        return (
          <>
            {cancelButton}
            <Button onClick={close}>Закрыть</Button>
            {view?.confirmationTaken || view?.siteOrderId ? null : (
              <Button variant="primary" icon={ShoppingCart} disabled={chosen.size === 0} onClick={() => setConfirming(true)}>
                Купить ({chosen.size})
              </Button>
            )}
          </>
        );
      case 'done':
        return (
          <>
            <Button onClick={close}>Закрыть</Button>
            {bought.length > 0 ? (
              <Button
                variant="primary"
                icon={ArrowRight}
                onClick={() => {
                  close();
                  navigate(`/sites?new=site&domain=${encodeURIComponent(bought[0]!)}`);
                }}
              >
                Создать сайт на {bought[0]}
              </Button>
            ) : null}
          </>
        );
    }
  })();

  return (
    <Dialog open={open} title={titles[stage]} description={description} onClose={close} size="lg" footer={footer}>
      <div className="stack">
        <WizardSteps stage={stage} />

        {stage === 'names' ? (
          <form id="purchase-start" className="stack" onSubmit={submit} noValidate>
            <Field
              label="Имена"
              htmlFor="purchase-names"
              hint={`С новой строки или через запятую, до ${PURCHASE_LIMITS.candidates}. Из списка выбираются свободные`}
              {...(shown && problems.names ? { error: problems.names } : {})}
            >
              <textarea id="purchase-names" className="input mono" rows={6} value={text} onChange={(event) => setText(event.target.value)} placeholder={'example.de\nexample-shop.de'} spellCheck={false} />
            </Field>
            <div className="form-grid">
              <Field label="Сколько купить" htmlFor="purchase-buy" {...(names.length > 0 ? { hint: `Из ${names.length} в списке` } : {})} {...(shown && problems.buy ? { error: problems.buy } : {})}>
                <Input id="purchase-buy" type="number" min={1} max={PURCHASE_LIMITS.buy} value={buy} onChange={(event) => setBuy(event.target.value)} />
              </Field>
              <Field label="Срок регистрации" htmlFor="purchase-years">
                <Select id="purchase-years" label="Срок регистрации" value={years} onChange={setYears} options={YEARS} />
              </Field>
            </div>
            <Checkbox checked={autoRenew} onChange={setAutoRenew} label="Продлевать автоматически" />
            <Checkbox checked={privacy} onChange={setPrivacy} label="Скрыть данные владельца (privacy)" />
            <Banner tone="info" icon={ShieldCheck}>
              <div className="stack stack-tight">
                <span>На этом шаге проверяются доступность и цена; оплаты нет.</span>
                {overview.tags.length > 0 ? (
                  <span>
                    Купленным доменам присваивается {overview.tags.length > 1 ? 'теги' : 'тег'} {overview.tags.map((tag) => `«${tag}»`).join(', ')}.
                  </span>
                ) : (
                  <span>Теги доменов не заданы: DNS настраивается в 2d2s.</span>
                )}
              </div>
            </Banner>
            {start.isError ? (
              <p className="field-error" role="alert">
                <CircleAlert size={14} aria-hidden="true" />
                {purchaseError(start.error, 'Не удалось начать проверку')}
              </p>
            ) : null}
          </form>
        ) : purchase.isPending ? (
          <Spinner label="Спрашиваем 2d2s…" />
        ) : purchase.isError && !view ? (
          <ErrorState error={purchase.error} />
        ) : view ? (
          <>
            {stage !== 'done' ? (
              <div className="row-between">
                <StatusPill map={PURCHASE_STATUS} status={view.status} />
                <LiveStatus updatedAt={purchase.dataUpdatedAt} live={!view.awaitingConfirmation} failed={purchase.isRefetchError} />
              </div>
            ) : null}
            {view.failure ? (
              <Banner tone="danger" icon={CircleAlert}>
                {view.failure}
              </Banner>
            ) : null}
            {cancel.isError ? <p className="field-error">{purchaseError(cancel.error, 'Не удалось отменить')}</p> : null}
            {stage === 'choose' && view.confirmationTaken ? (
              <Banner tone="warning" icon={Clock3}>
                Оплата запрошена, но 2d2s её ещё не подтвердил. Повторно купить нельзя, чтобы не заплатить дважды: ход покупки обновится здесь, когда 2d2s ответит.
              </Banner>
            ) : stage === 'choose' && view.siteOrderId ? (
              <Banner tone="info" icon={ShieldCheck}>
                Эта проверка — часть заказа нового сайта: домен покупается из окна «Новый сайт».
              </Banner>
            ) : null}

            {stage === 'checking' ? <CheckingBody view={view} /> : null}
            {stage === 'choose' ? (
              <ChooseBody
                view={view}
                overview={overview}
                chosen={chosen}
                onToggle={(itemId, on) => {
                  const next = new Set(chosen);
                  if (on) next.add(itemId);
                  else next.delete(itemId);
                  setPicked(next);
                }}
              />
            ) : null}
            {stage === 'buying' ? <BuyingBody view={view} /> : null}
            {stage === 'done' ? <DoneBody view={view} overview={overview} /> : null}

            <PurchaseLog events={view.events} open={stage === 'buying' || stage === 'done'} />
          </>
        ) : null}
      </div>

      {view && stage === 'choose' ? (
        <ConfirmPurchase
          view={view}
          overview={overview}
          chosen={chosen}
          shortlist={shortlist}
          open={confirming}
          busy={confirm.isPending}
          error={confirm.isError ? purchaseError(confirm.error, 'Не удалось купить') : undefined}
          onConfirm={() => confirm.mutate({ itemIds: [...chosen] })}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </Dialog>
  );
}

/** The free names 2d2s shortlisted, as many as are to be bought; the first free ones when it marked none. */
function shortlistOf(view: DomainPurchaseView): ReadonlySet<string> {
  const free = view.items.filter((item) => item.available === true);
  const shortlisted = free.filter((item) => item.shortlisted);
  return new Set((shortlisted.length > 0 ? shortlisted : free).slice(0, view.buy).map((item) => item.id));
}

function CheckingBody({ view }: { readonly view: DomainPurchaseView }): ReactNode {
  const progress = purchaseProgress(view);
  const checked = view.items.filter((item) => item.available !== null).length;
  return (
    <div className="stack">
      <div className="stack stack-tight">
        <div className="row-between text-sm">
          <span>{view.items.length > 0 ? `Проверено ${checked} из ${view.items.length}` : '2d2s ставит проверку в очередь'}</span>
          {view.progress !== null ? <span className="numeric">{Math.round(view.progress)}%</span> : null}
        </div>
        <ProgressBar value={progress.value} max={progress.max} label="Проверка доступности" indeterminate={view.items.length === 0} />
      </div>
      {view.items.length > 0 ? (
        <ul className="stack stack-tight list-plain" aria-label="Имена">
          {view.items.map((item) => (
            <li key={item.id} className="row-between">
              <span className="mono">{item.domain}</span>
              {item.available === null ? (
                <span className="row-tight muted">
                  <Loader2 size={14} className="spin" aria-hidden="true" />
                  проверяется
                </span>
              ) : (
                <span className={item.available ? 'tone-healthy' : 'muted'}>{checkingState(item)}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** The shortlist, and which of it to pay for: no more than the purchase is for. The free names' history on request. */
function ChooseBody({
  view,
  overview,
  chosen,
  onToggle,
}: {
  readonly view: DomainPurchaseView;
  readonly overview: DomainPurchasesOverview;
  readonly chosen: ReadonlySet<string>;
  readonly onToggle: (itemId: string, on: boolean) => void;
}): ReactNode {
  const history = useDomainCheck();
  const free = view.items.filter((item) => item.available === true);
  const taken = view.items.filter((item) => item.available !== true);

  return (
    <div className="stack">
      <Facts>
        <Fact label="Свободно">
          {free.length} из {view.items.length}
        </Fact>
        <Fact label="Купить">
          до {view.buy} · на {YEARS.find((option) => option.value === String(view.years))?.label ?? `${view.years} г.`}
        </Fact>
        <Fact label="Цена по расчёту 2d2s">{formatMoney(view.plannedCost, view.currency)}</Fact>
        {overview.budget ? <Fact label="Баланс для покупок">{formatMoney(overview.budget.balance, overview.budget.currency)}</Fact> : null}
      </Facts>

      {free.length > 0 ? (
        <div className="row-tight row-wrap">
          <Button size="sm" icon={SearchCheck} busy={history.running} onClick={() => history.run(free.slice(0, 20).map((item) => item.domain).join('\n'))}>
            Проверить историю свободных
          </Button>
          {history.domains.length > 0 ? (
            <span className="muted text-sm">
              {history.running ? 'Проверяем' : 'Проверено'} {history.done} из {history.domains.length}: Web Archive, DR, спам
            </span>
          ) : (
            <span className="muted text-sm">Web Archive, DR и спам — до покупки</span>
          )}
        </div>
      ) : null}

      <fieldset className="field fieldset">
        <legend className="field-label">
          Что купить — выбрано {chosen.size} из {view.buy}
        </legend>
        <div className="stack stack-tight">
          {free.map((item) => {
            const found = history.results[item.domain];
            const years = found?.wayback?.data?.years ?? [];
            return (
              <div key={item.id} className="row-between row-wrap">
                <Checkbox
                  checked={chosen.has(item.id)}
                  disabled={!chosen.has(item.id) && chosen.size >= view.buy}
                  onChange={(on) => onToggle(item.id, on)}
                  label={<span className="mono">{item.domain}</span>}
                />
                {found ? (
                  <span className="row-tight text-sm">
                    <StatusPill map={DOMAIN_VERDICT} status={found.verdict ?? 'none'} />
                    <span className="muted">
                      {years.length > 0 ? `в архиве с ${years[0]}` : 'снимков нет'}
                      {found.ahrefs?.data?.domainRating !== null && found.ahrefs?.data?.domainRating !== undefined ? ` · DR ${found.ahrefs.data.domainRating}` : ''}
                    </span>
                  </span>
                ) : history.domains.includes(item.domain) ? (
                  <span className="row-tight muted text-sm">
                    <Loader2 size={14} className="spin" aria-hidden="true" />
                    история…
                  </span>
                ) : null}
              </div>
            );
          })}
          {taken.map((item) => (
            <div key={item.id} className="row-between">
              <span className="mono muted">{item.domain}</span>
              <span className="muted">{item.failure ?? checkingState(item)}</span>
            </div>
          ))}
          {free.length === 0 ? <p className="muted">Свободных имён нет. Отмените покупку и проверьте другие.</p> : null}
        </div>
      </fieldset>
    </div>
  );
}

/** Paying and the DNS, name by name, with the steps and a bar. */
function BuyingBody({ view }: { readonly view: DomainPurchaseView }): ReactNode {
  const chosen = chosenItems(view);
  const progress = purchaseProgress(view);
  const bought = chosen.filter((item) => item.purchased).length;
  return (
    <div className="stack">
      <Steps
        label="Ход покупки"
        items={[
          { label: 'Подтверждение', state: 'done' },
          {
            label: 'Оплата у регистратора',
            state: view.status === 'PURCHASING' ? 'running' : 'done',
            detail: `Куплено ${bought} из ${chosen.length}`,
          },
          {
            label: 'Зоны Cloudflare и NS',
            state: view.status === 'PROVISIONING_DNS' ? 'running' : view.status === 'PURCHASING' ? 'pending' : 'done',
            detail: view.status === 'PROVISIONING_DNS' ? '2d2s создаёт зоны и прописывает NS у регистратора' : undefined,
          },
          { label: 'Готово', state: 'pending' },
        ]}
      />
      <ProgressBar value={progress.value} max={progress.max} label="Ход покупки" />
      <ul className="stack stack-tight list-plain" aria-label="Домены">
        {chosen.map((item) => {
          const state = buyingState(item, view);
          return (
            <li key={item.id} className="row-between">
              <span className="mono">{item.domain}</span>
              <span className={`row-tight ${state.tone === 'failed' ? 'tone-down' : state.tone === 'done' ? 'tone-healthy' : 'muted'}`}>
                {state.tone === 'running' ? <Loader2 size={14} className="spin" aria-hidden="true" /> : null}
                {state.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The result: what was bought and paid, what was not and why, and what to do next. */
function DoneBody({ view, overview }: { readonly view: DomainPurchaseView; readonly overview: DomainPurchasesOverview }): ReactNode {
  const chosen = chosenItems(view);
  const bought = view.items.filter((item) => item.purchased);
  return (
    <div className="stack">
      <Facts>
        <Fact label="Итог">
          <StatusPill map={PURCHASE_STATUS} status={view.status} />
        </Fact>
        <Fact label="Куплено">
          {bought.length} {plural(bought.length, 'домен', 'домена', 'доменов')}
          {chosen.length > 0 ? <span className="muted"> из {chosen.length} выбранных</span> : null}
        </Fact>
        {view.totalCost !== null ? <Fact label="Оплачено">{formatMoney(view.totalCost, view.currency)}</Fact> : null}
      </Facts>
      {chosen.length > 0 ? (
        <ul className="stack stack-tight list-plain" aria-label="Домены">
          {chosen.map((item) => {
            const state = buyingState(item, view);
            return (
              <li key={item.id} className="row-between">
                <span className="mono">{item.domain}</span>
                <span className={state.tone === 'failed' ? 'tone-down' : 'tone-healthy'}>{state.label}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {bought.length > 0 ? (
        <Banner tone="info" icon={CircleCheck}>
          Домены в 2d2s{overview.tags.length > 0 ? ` с тегом ${overview.tags.map((tag) => `«${tag}»`).join(', ')}` : ''} и доступны при создании сайта.
          {overview.tags.length === 0 ? ' DNS для них настраивается в 2d2s.' : ''}
        </Banner>
      ) : null}
    </div>
  );
}

function ConfirmPurchase({
  view,
  overview,
  chosen,
  shortlist,
  open,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  readonly view: DomainPurchaseView;
  readonly overview: DomainPurchasesOverview;
  readonly chosen: ReadonlySet<string>;
  readonly shortlist: ReadonlySet<string>;
  readonly open: boolean;
  readonly busy: boolean;
  readonly error: string | undefined;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}): ReactNode {
  const names = view.items.filter((item) => chosen.has(item.id)).map((item) => item.domain);
  const short = overview.budget && view.plannedCost !== null && view.plannedCost > overview.budget.balance;
  // 2d2s prices its own shortlist; a different choice may cost otherwise.
  const ownChoice = chosen.size !== shortlist.size || [...chosen].some((itemId) => !shortlist.has(itemId));
  return (
    <ConfirmDialog
      open={open}
      title={`Купить ${names.length} ${plural(names.length, 'домен', 'домена', 'доменов')}?`}
      description="Оплата спишется с баланса 2d2s. Отменить покупку после подтверждения нельзя."
      confirmLabel="Купить"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <div className="stack stack-tight">
        <ul className="stack stack-tight list-plain">
          {names.map((name) => (
            <li key={name} className="mono">
              {name}
            </li>
          ))}
        </ul>
        <span className="muted">
          Цена по расчёту 2d2s — {formatMoney(view.plannedCost, view.currency)}
          {overview.budget ? `, на балансе ${formatMoney(overview.budget.balance, overview.budget.currency)}` : ''}.
          {ownChoice ? ' Цена рассчитана для имён, выбранных 2d2s; для других может отличаться.' : ''}
        </span>
        {short ? <span className="field-error">Недостаточно средств на балансе.</span> : null}
      </div>
    </ConfirmDialog>
  );
}
